import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  Divider,
  message,
  Modal,
} from 'antd';
import {
  BiCommentCheck,
  BiCommentX,
} from 'react-icons/bi';
import { GoLink } from 'react-icons/go';
import { IoMdHeartEmpty } from 'react-icons/io';
import { IoHeartDislikeOutline } from 'react-icons/io5';
import {
  PiClosedCaptioning,
  PiPencilSimple,
  PiTrash,
  PiWarningOctagon,
} from 'react-icons/pi';
import {
  TbMessageCircle,
  TbMessageCircleOff,
  TbPin,
  TbPinnedOff,
} from 'react-icons/tb';
import { useParams } from 'react-router-dom';
import { BottomSheet } from 'react-spring-bottom-sheet';

import CommunityAPI from '../../../../api/CommunityAPI';
import DeleteSvg from '../../../../assets/svg/Delete';
import ViewMoreSvg from '../../../../assets/svg/ViewMore';
import BottomSheetTile
  from '../../../../components/BottomSheetTile/BottomSheetTile';
import ConfirmationModal
  from '../../../../components/Modals/ConfirmationModal/ConfirmationModal';
import Report from '../../../../components/Report/Report';
import {
  setPDPCommunityPostData,
  updateCommunitPost,
  userInterationAction,
} from '../../../../context/communityReducer';
import { useTheme } from '../../../../context/ThemeProvider';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  copyToClipboard,
  handleShareLink,
} from '../../../../shared/utils';
import {
  IChannelPostDetails,
  IPostSettings,
} from '../../../../types/communityTypes';
import { ROUTES } from '../../../../types/routes';
import {
  Onboarding,
  UAMPermissions,
} from '../../../../types/userTypes';
import EditCommunityPost from '../../../CreateCommunityPost/EditCommunityPost';

type Props = {
  showMenu: boolean;
  closeModel: () => void;
  communityPostDetails: IChannelPostDetails;
  onTogglePinPost: () => void;
  isPDP?: boolean;
};

const CommunityPostMenuModel: React.FC<Props> = ({
  showMenu,
  closeModel,
  communityPostDetails,
  isPDP,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const userDetails = useAppSelector((state) => state?.user);
  const { selectedChannelData } = useAppSelector((state) => state.community);
  const { hostMetadata } = useAppSelector((state) => state?.app);

  const { channelId: paramsChannelId } = useParams();
  const { colors } = useTheme();

  const { post, ...postDetails } = communityPostDetails;
  const [showEditPostModal, setShowEditPostModal] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const channelId = useMemo(() => {
    return communityPostDetails?.channel || selectedChannelData?._id;
  }, [communityPostDetails?.channel, selectedChannelData?._id]);

  const isCreatorOrUAM = useMemo(() => {
    return (
      userDetails?.id === post?.postedBy?._id ||
      userDetails?.id === hostMetadata?.creator?._id ||
      userDetails?.userAccessPermissions?.includes(
        UAMPermissions?.COMMUNITY_ACCESS,
      )
    );
  }, [
    userDetails?.id,
    userDetails?.userAccessPermissions,
    post?.postedBy?._id,
    hostMetadata?.creator?._id,
  ]);

  const copyLinktoClipboard = async () => {
    handleShareLink(
      `web${ROUTES.CHANNEL_POST_METADATA_VIEW.replace(
        ':postId',
        post._id,
      ).replace(':channelId', channelId as string)}`,
    );
  };

  const handleRemovePost = useCallback(() => {
    const thunkAction =
      window.location?.pathname.includes('/channel/') ||
      window.location?.pathname.includes('/profile/')
        ? updateCommunitPost
        : setPDPCommunityPostData;
    dispatch(
      userInterationAction(
        'removePost',
        communityPostDetails?._id,
        (channelId || paramsChannelId) as string,
        communityPostDetails,
        {},
        () => {
          dispatch(thunkAction({}));
        },
        {},
        () => {
          message.success('Deleted post successfully');
          if (
            !(
              window.location?.pathname.includes('/channel/') ||
              window.location?.pathname.includes('/profile/')
            )
          ) {
            navigate.goBack();
          }
          setIsDialogVisible(false);
          closeModel();
        },
        window.location?.pathname.includes('/channel/') ||
          window.location?.pathname.includes('/profile/'),
      ),
    );
  }, [
    channelId,
    closeModel,
    communityPostDetails,
    dispatch,
    navigate,
    paramsChannelId,
  ]);

  const handleToggledPinned = useCallback(
    (isPinned: boolean, successMessage: string) => {
      const thunkAction =
        window.location?.pathname.includes('/channel/') ||
        window.location?.pathname.includes('/profile/')
          ? updateCommunitPost
          : setPDPCommunityPostData;

      dispatch(
        userInterationAction(
          isPinned ? 'addPinned' : 'removePinned',
          communityPostDetails?._id,
          (channelId || paramsChannelId) as string,
          communityPostDetails,
          {},
          (updatedData) => {
            dispatch(thunkAction({ ...updatedData, isPinned }));
          },
          { isPinned },
          () => {
            message.success(successMessage);
          },
          window.location?.pathname.includes('/channel/') ||
            window.location?.pathname.includes('/profile/'),
        ),
      );
      closeModel();
    },
    [channelId, closeModel, communityPostDetails, dispatch, paramsChannelId],
  );

  const handleReportPost = () => {
    setShowReportModal(true);
    // console.log('handle report post');
  };

  const handleUpdatePostSettings = useCallback(
    (updatedPostSettings: IPostSettings, successFunction: () => void) => {
      const updatedAttributes = {
        post: {
          ...post,
          postSettings: { ...post?.postSettings, ...updatedPostSettings },
        },
      };
      const thunkAction =
        window.location?.pathname.includes('/channel/') ||
        window.location?.pathname.includes('/profile/')
          ? updateCommunitPost
          : setPDPCommunityPostData;
      dispatch(
        userInterationAction(
          'updatePostSettings',
          communityPostDetails?._id,
          (channelId || paramsChannelId) as string,
          communityPostDetails,
          updatedAttributes,
          (updatedData) => {
            dispatch(thunkAction(updatedData));
          },
          { postSettings: { ...post?.postSettings, ...updatedPostSettings } },
          successFunction,
        ),
      );
      closeModel();
    },
    [
      channelId,
      closeModel,
      communityPostDetails,
      dispatch,
      paramsChannelId,
      post,
    ],
  );

  return (
    <>
      <BottomSheet open={showMenu} onDismiss={closeModel}>
        {isPDP ? null : (
          <BottomSheetTile
            icon={<ViewMoreSvg width={20} height={20} />}
            title="View more"
            onClick={() => {
              navigate(ROUTES.COMMUNITY_POST_DETAILS, {
                postId: communityPostDetails?._id,
                channelId: channelId as string,
              });
            }}
          />
        )}
        <BottomSheetTile
          icon={<GoLink color={colors.ICON} size={20} />}
          title="Copy link"
          onClick={() => {
            copyLinktoClipboard();
            closeModel();
          }}
        />
        {post.caption && !post?.poll ? (
          <BottomSheetTile
            icon={<PiClosedCaptioning color={colors.ICON} size={20} />}
            title="Copy caption"
            onClick={() => {
              copyToClipboard(post.caption);
              closeModel();
            }}
          />
        ) : null}

        {!post.poll &&
        isCreatorOrUAM &&
        (userDetails?.id === post?.postedBy?._id ||
          (post?.postedBy?._id === hostMetadata?.creator?._id &&
            userDetails?.type === Onboarding.CREATOR_RESTRICTED)) ? (
          <BottomSheetTile
            icon={<PiPencilSimple color={colors.ICON} size={20} />}
            title="Edit post"
            onClick={() => {
              setShowEditPostModal(true);
              closeModel();
            }}
          />
        ) : null}
        <>
          {isCreatorOrUAM ? (
            <>
              <Divider />
              <BottomSheetTile
                icon={
                  postDetails.isPinned ? (
                    <TbPinnedOff color={colors.ICON} size={20} />
                  ) : (
                    <TbPin color={colors.ICON} size={20} />
                  )
                }
                title={`${postDetails.isPinned ? 'Unpin' : 'Pin'} post`}
                onClick={() => {
                  handleToggledPinned(
                    !postDetails.isPinned,
                    `${
                      postDetails.isPinned ? 'Unpin' : 'Pin'
                    } post successfully`,
                  );
                }}
              />

              <BottomSheetTile
                icon={
                  post?.postSettings?.hideComment ? (
                    <TbMessageCircleOff color={colors.ICON} size={20} />
                  ) : (
                    <TbMessageCircle color={colors.ICON} size={20} />
                  )
                }
                title={
                  post?.postSettings?.hideComment
                    ? 'Unhide comments'
                    : 'Hide comments'
                }
                onClick={() => {
                  handleUpdatePostSettings(
                    {
                      ...post?.postSettings,
                      hideComment: !post?.postSettings?.hideComment,
                    },
                    () => {
                      message.success(
                        `${
                          post?.postSettings?.hideComment
                            ? 'Unhide comments'
                            : 'Hide comments'
                        } successfully`,
                      );
                    },
                  );
                }}
              />

              <BottomSheetTile
                icon={
                  post?.postSettings?.disableComment ? (
                    <BiCommentCheck color={colors.ICON} size={20} />
                  ) : (
                    <BiCommentX color={colors.ICON} size={20} />
                  )
                }
                title={
                  post?.postSettings?.disableComment
                    ? 'Enable comments'
                    : 'Disable comments'
                }
                onClick={() => {
                  handleUpdatePostSettings(
                    {
                      ...post?.postSettings,
                      disableComment: !post?.postSettings?.disableComment,
                    },
                    () => {
                      message.success(
                        `${
                          post?.postSettings?.disableComment
                            ? 'Enable comments'
                            : 'Disable comments'
                        } successfully`,
                      );
                    },
                  );
                }}
                // onClick={() => {
                //   onEnableOrDisableComments(false);
                // }}
              />

              <BottomSheetTile
                icon={
                  post?.postSettings?.hideLikes ? (
                    <IoHeartDislikeOutline color={colors.ICON} size={20} />
                  ) : (
                    <IoMdHeartEmpty color={colors.ICON} size={20} />
                  )
                }
                title={
                  post?.postSettings?.hideLikes ? 'Unhide likes' : 'Hide likes'
                }
                onClick={() => {
                  handleUpdatePostSettings(
                    {
                      ...post?.postSettings,
                      hideLikes: !post?.postSettings?.hideLikes,
                    },
                    () => {
                      message.success(
                        `${
                          post?.postSettings?.hideLikes
                            ? 'Unhide likes'
                            : 'Hide likes'
                        } successfully`,
                      );
                    },
                  );
                }}
                // onClick={() => {
                //   onEnableOrDisableComments(false);
                // }}
              />
            </>
          ) : null}
        </>
        {isCreatorOrUAM ? (
          <>
            <hr className="dropdownDivider" />
            <BottomSheetTile
              icon={
                isCreatorOrUAM ? (
                  <PiTrash />
                ) : (
                  <DeleteSvg width={20} height={20} />
                )
              }
              warning
              title={isCreatorOrUAM ? 'Delete post' : 'Remove post'}
              onClick={() => {
                closeModel();
                setIsDialogVisible(true);
              }}
            />
          </>
        ) : null}
        {userDetails?.id !== post?.postedBy?._id &&
        userDetails?.id !== hostMetadata?.creator?._id &&
        post?.postedBy?._id !== hostMetadata?.creator?._id ? (
          <>
            <hr className="dropdownDivider" />
            <BottomSheetTile
              icon={<PiWarningOctagon />}
              warning
              title={'Report post'}
              onClick={() => {
                closeModel();
                handleReportPost();
              }}
            />
          </>
        ) : null}
      </BottomSheet>
      <Modal
        open={showEditPostModal}
        title={null}
        footer={null}
        closable={false}
        destroyOnClose
        className="postDetailsModal">
        <EditCommunityPost
          post={post}
          handleBack={() => {
            setShowEditPostModal(false);
          }}
          postChannelId={postDetails?.channel}
          postId={communityPostDetails?._id}
        />
      </Modal>
      <ConfirmationModal
        title={isCreatorOrUAM ? 'Delete' : 'Remove'}
        open={isDialogVisible}
        handleOk={handleRemovePost}
        handleCancel={() => {
          setIsDialogVisible(false);
        }}
        message={`Do you really want to ${
          isCreatorOrUAM ? 'delete' : 'remove'
        } the post?`}
        okayButtonText={isCreatorOrUAM ? 'Delete' : 'Remove'}
        cancelButtonText="Cancel"
      />
      <Report
        showModal={showReportModal}
        closeModal={() => setShowReportModal(false)}
        type="channelpost"
        post={{ _id: communityPostDetails?._id }}
        onReportAction={async (data) => {
          const resp = await CommunityAPI.reportCommentOrPost(data);
          if (resp.status === 201) {
            message.success('Post reported successfully');
            setShowReportModal(false);
          } else {
            showAppError(resp?.data);
          }
        }}
      />
    </>
  );
};

export default CommunityPostMenuModel;
